import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  getDefaultChartMode,
  mapToBuilderKey
} from '@aytm/app/stats/page/question-views/info-view/components/charts/bars/lib/chartModes'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'

const InfoViewBarsChart = ({
  questionId,
  questionKey,
  qType,
  chartMode: chartModeProp,
  questionChartMode,
  globalChartMode,
  answerVariants,
  sortOrder,
  batches,
  groupType,
  // shared
  answer,
  answerType,
  format,
  ignoreAmount,
  ignorePercents,
  xAxisTitle,
  yAxisTitle,
  yAxisLimits,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features,
  isTraitType
}) => {
  const hasBatches = Array.isArray(batches) && batches.length > 0

  const chartMode = useMemo(
    () => getDefaultChartMode({ qType, chartMode: chartModeProp, questionChartMode, globalChartMode }),
    [qType, chartModeProp, questionChartMode, globalChartMode]
  )

  const builderChartMode = useMemo(() => mapToBuilderKey(chartMode, hasBatches), [chartMode, hasBatches])

  // Data Lab builders gate markers on `useSigTest === true`; only the batched branch defaulted it on.
  const batchedFormat = useMemo(() => ({ ...(format || {}), useSigTest: format?.useSigTest !== false }), [format])

  const sharedProps = {
    questionId,
    questionKey,
    qType,
    chartMode: builderChartMode,
    chartModeTypes: [],
    answer,
    answerType,
    format,
    ignoreRefs: true,
    ignoreAmount,
    ignorePercents,
    xAxisTitle,
    yAxisTitle,
    yAxisLimits,
    colorTheme,
    chartLegendMode,
    confidenceLevel,
    significanceLevel,
    confidenceLevels,
    features
  }

  return hasBatches ? (
    <ChartContainer
      {...sharedProps}
      text=""
      format={batchedFormat}
      batches={batches}
      groupType={groupType || format?.groupType || 'interlaced'}
      axisType={chartMode === 'percents' || chartMode === 'bpercents' ? 'percents' : 'respondents'}
    />
  ) : (
    <ChartContainer
      {...sharedProps}
      ignoreTraits={format?.ignoreTraits === true || !isTraitType}
      answerVariants={answerVariants}
      sortOrder={sortOrder}
    />
  )
}

InfoViewBarsChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  chartMode: PropTypes.string,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  answerVariants: PropTypes.array,
  sortOrder: PropTypes.string,
  batches: PropTypes.array,
  groupType: PropTypes.string,
  answer: answerPropType,
  answerType: PropTypes.string,
  format: PropTypes.object,
  ignoreAmount: PropTypes.bool,
  ignorePercents: PropTypes.bool,
  xAxisTitle: PropTypes.string,
  yAxisTitle: PropTypes.string,
  yAxisLimits: PropTypes.array,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  isTraitType: PropTypes.bool
}

InfoViewBarsChart.displayName = 'InfoViewBarsChart'

export default InfoViewBarsChart
